import React, { useState, useEffect, useRef } from 'react';
import { PlusIcon, CalendarIcon, ArrowUpTrayIcon, CloudArrowDownIcon, TrashIcon } from '@heroicons/react/24/outline';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import DataTable from '../components/common/DataTable';
import { attendanceService, employeeService } from '../services/hrService';
import { formatDate, formatTime } from '../utils/format';

export default function Attendance() {
  const [attendances, setAttendances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(''); // Start of date range
  const [endDate, setEndDate] = useState(''); // End of date range
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // Track if editing
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const fileInputRef = useRef(null);
  const [crosschexConfig, setCrosschexConfig] = useState({
    api_key: localStorage.getItem('crosschex_api_key') || '',
    api_secret: localStorage.getItem('crosschex_api_secret') || '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });
  const [formData, setFormData] = useState({
    employee_id: '',
    date: new Date().toISOString().split('T')[0],
    time_in: '',
    time_out: '',
    overtime_hours: '0',
    late_minutes: '0',
    is_late: false,
    is_undertime: false,
    is_absent: false,
    remarks: ''
  });

  useEffect(() => {
    fetchAttendances();
    fetchEmployees();
  }, [startDate, endDate]);

  const fetchAttendances = async () => {
    try {
      setLoading(true);
      const params = { search: searchTerm };
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }
      const response = await attendanceService.getAll(params);
      setAttendances(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching attendances:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await employeeService.getAll({ is_active: true });
      setEmployees(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAttendances();
  };

  const openModal = () => {
    setEditingId(null);
    setFormData({
      employee_id: '',
      date: new Date().toISOString().split('T')[0],
      time_in: '',
      time_out: '',
      overtime_hours: '0',
      late_minutes: '0',
      is_late: false,
      is_undertime: false,
      is_absent: false,
      remarks: ''
    });
    setIsModalOpen(true);
  };

  const openEditModal = (attendance) => {
    setEditingId(attendance.id);
    setFormData({
      employee_id: attendance.employee || '',
      date: attendance.date || '',
      time_in: attendance.time_in ? attendance.time_in.substring(0, 5) : '',
      time_out: attendance.time_out ? attendance.time_out.substring(0, 5) : '',
      overtime_hours: attendance.overtime_hours?.toString() || '0',
      late_minutes: attendance.late_minutes?.toString() || '0',
      is_late: attendance.is_late || false,
      is_undertime: attendance.is_undertime || false,
      is_absent: attendance.is_absent || false,
      remarks: attendance.remarks || ''
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData({
      employee_id: '',
      date: new Date().toISOString().split('T')[0],
      time_in: '',
      time_out: '',
      overtime_hours: '0',
      late_minutes: '0',
      is_late: false,
      is_undertime: false,
      is_absent: false,
      remarks: ''
    });
  };

  // Work schedule: 8am-12nn, 1pm-5pm
  // OT: 5:01pm onwards, but ONLY counted if logout is after 7pm
  const WORK_START = 8 * 60;  // 8:00 AM in minutes
  const WORK_END = 17 * 60;   // 5:00 PM in minutes (regular end)
  const OT_QUALIFY = 19 * 60; // 7:00 PM - must logout after this to get OT

  const calculateAttendance = (timeIn, timeOut) => {
    let lateMinutes = 0;
    let isLate = false;
    let overtimeHours = 0;
    let isUndertime = false;

    // Calculate late minutes (if time_in is after 8:00 AM)
    if (timeIn) {
      const [inHours, inMins] = timeIn.split(':').map(Number);
      const timeInMinutes = inHours * 60 + inMins;

      if (timeInMinutes > WORK_START) {
        lateMinutes = timeInMinutes - WORK_START;
        isLate = true;
      }
    }

    // Calculate overtime
    // OT is from 5:01pm onwards, but ONLY if employee logs out AFTER 7:00 PM
    if (timeOut) {
      const [outHours, outMins] = timeOut.split(':').map(Number);
      const timeOutMinutes = outHours * 60 + outMins;

      // OT only counts if logout is after 7:00 PM
      if (timeOutMinutes > OT_QUALIFY) {
        // OT hours = time from 5:00 PM to logout time
        overtimeHours = (timeOutMinutes - WORK_END) / 60;
      }

      // Check for undertime (left before 5:00 PM)
      if (timeOutMinutes < WORK_END) {
        isUndertime = true;
      }
    }

    return { lateMinutes, isLate, overtimeHours, isUndertime };
  };

  const handleTimeInChange = (value) => {
    const calc = calculateAttendance(value, formData.time_out);
    setFormData({
      ...formData,
      time_in: value,
      late_minutes: calc.lateMinutes.toString(),
      is_late: calc.lateMinutes > 0,
      overtime_hours: calc.overtimeHours.toFixed(2),
      is_undertime: calc.isUndertime
    });
  };

  const handleTimeOutChange = (value) => {
    const calc = calculateAttendance(formData.time_in, value);
    setFormData({
      ...formData,
      time_out: value,
      late_minutes: calc.lateMinutes.toString(),
      is_late: calc.lateMinutes > 0,
      overtime_hours: calc.overtimeHours.toFixed(2),
      is_undertime: calc.isUndertime
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        employee: formData.employee_id,
        date: formData.date,
        time_in: formData.time_in || null,
        time_out: formData.time_out || null,
        overtime_hours: parseFloat(formData.overtime_hours) || 0,
        late_minutes: parseInt(formData.late_minutes) || 0,
        is_late: formData.is_late,
        is_undertime: formData.is_undertime,
        is_absent: formData.is_absent,
        remarks: formData.remarks
      };

      if (editingId) {
        await attendanceService.update(editingId, submitData);
      } else {
        await attendanceService.create(submitData);
      }
      closeModal();
      fetchAttendances();
    } catch (error) {
      console.error('Error saving attendance:', error);
      const errData = error.response?.data;
      let errMsg = 'Failed to save attendance';
      if (errData) {
        if (errData.non_field_errors) {
          errMsg = errData.non_field_errors.join(', ');
        } else if (errData.detail) {
          errMsg = errData.detail;
        } else if (typeof errData === 'object') {
          errMsg = Object.entries(errData).map(([k, v]) => `${k}: ${v}`).join(', ');
        }
      }
      alert(errMsg);
    }
  };

  const handleDelete = async (attendance) => {
    const employeeName = attendance.employee_name || 'this employee';
    const dateStr = formatDate(attendance.date);
    if (!window.confirm(`Are you sure you want to delete the attendance record for ${employeeName} on ${dateStr}?`)) {
      return;
    }
    try {
      await attendanceService.delete(attendance.id);
      fetchAttendances();
    } catch (error) {
      console.error('Error deleting attendance:', error);
      alert(error.response?.data?.detail || 'Failed to delete attendance record');
    }
  };

  const [bulkDate, setBulkDate] = useState(new Date().toISOString().split('T')[0]);

  const handleBulkEntry = async (e) => {
    e.preventDefault();
    const entries = employees.map(emp => ({
      employee_id: emp.id,
      date: bulkDate,
      time_in: '08:00',
      time_out: '17:00',
      status: 'present'
    }));

    try {
      await attendanceService.bulkCreate({ records: entries });
      setIsBulkModalOpen(false);
      fetchAttendances();
    } catch (error) {
      console.error('Error bulk creating attendance:', error);
      alert('Failed to create bulk attendance');
    }
  };

  const handleImportCrosschex = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await attendanceService.importCrosschex(formData);
      setImportResult(response.data);
      fetchAttendances();
    } catch (error) {
      console.error('Error importing attendance:', error);
      setImportResult({
        success: false,
        error: error.response?.data?.error || 'Failed to import attendance records'
      });
    } finally {
      setImporting(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSyncCrosschex = async () => {
    if (!crosschexConfig.api_key || !crosschexConfig.api_secret) {
      alert('Please enter your Crosschex Cloud API Key and Secret');
      return;
    }

    setSyncing(true);
    setSyncResult(null);

    try {
      // Save credentials to localStorage for convenience
      localStorage.setItem('crosschex_api_key', crosschexConfig.api_key);
      localStorage.setItem('crosschex_api_secret', crosschexConfig.api_secret);

      const response = await attendanceService.syncCrosschex({
        api_key: crosschexConfig.api_key,
        api_secret: crosschexConfig.api_secret,
        start_date: crosschexConfig.start_date,
        end_date: crosschexConfig.end_date
      });

      setSyncResult(response.data);
      if (response.data.success) {
        fetchAttendances();
      }
    } catch (error) {
      console.error('Error syncing from Crosschex:', error);
      setSyncResult({
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || 'Failed to sync from Crosschex Cloud'
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleTestCrosschex = async () => {
    if (!crosschexConfig.api_key || !crosschexConfig.api_secret) {
      alert('Please enter your Crosschex Cloud API Key and Secret');
      return;
    }

    setSyncing(true);
    setSyncResult(null);

    try {
      const response = await attendanceService.testCrosschex({
        api_key: crosschexConfig.api_key,
        api_secret: crosschexConfig.api_secret
      });

      if (response.data.success) {
        setSyncResult({
          success: true,
          message: response.data.message,
          devices: response.data.devices
        });
        // Save credentials if test successful
        localStorage.setItem('crosschex_api_key', crosschexConfig.api_key);
        localStorage.setItem('crosschex_api_secret', crosschexConfig.api_secret);
      } else {
        setSyncResult({
          success: false,
          error: response.data.message || 'Connection test failed'
        });
      }
    } catch (error) {
      console.error('Error testing Crosschex connection:', error);
      setSyncResult({
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || 'Failed to connect to Crosschex Cloud'
      });
    } finally {
      setSyncing(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      present: 'bg-green-100 text-green-800',
      absent: 'bg-red-100 text-red-800',
      late: 'bg-yellow-100 text-yellow-800',
      half_day: 'bg-blue-100 text-blue-800',
      on_leave: 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const columns = [
    {
      key: 'employee',
      label: 'Employee Name',
      render: (att) => (
        <div>
          <div className="font-medium text-gray-900">{att.employee_name || '-'}</div>
          <div className="text-xs text-gray-500">{att.employee_no || ''}</div>
        </div>
      )
    },
    {
      key: 'date',
      label: 'Date',
      render: (att) => formatDate(att.date)
    },
    {
      key: 'time_in',
      label: 'Time In (8AM)',
      render: (att) => (att.time_in && att.time_in !== '00:00:00' && att.time_in !== '00:00') ? formatTime(att.time_in) : '-'
    },
    {
      key: 'time_out',
      label: 'Time Out (5PM)',
      render: (att) => (att.time_out && att.time_out !== '00:00:00' && att.time_out !== '00:00') ? formatTime(att.time_out) : '-'
    },
    {
      key: 'hours_worked',
      label: 'Hours Worked',
      render: (att) => att.hours_worked ? parseFloat(att.hours_worked).toFixed(2) : '0.00'
    },
    {
      key: 'late_info',
      label: 'Late (mins)',
      render: (att) => att.is_late ? (
        <span className="text-yellow-600 font-medium">
          {att.late_minutes} min
        </span>
      ) : '-'
    },
    {
      key: 'overtime_hours',
      label: 'Overtime (hrs)',
      render: (att) => {
        if (!att.overtime_hours || parseFloat(att.overtime_hours) === 0) return '-';
        const eligible = att.is_ot_eligible;
        return (
          <span className={eligible ? 'text-green-600 font-medium' : 'text-gray-400'}>
            {parseFloat(att.overtime_hours).toFixed(2)} hrs {eligible ? '✓' : '✗'}
          </span>
        );
      }
    },
    {
      key: 'flags',
      label: 'Status',
      render: (att) => {
        if (att.is_absent) return <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">Absent</span>;
        if (att.is_late && att.is_undertime) return <span className="px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-800">Late + Undertime</span>;
        if (att.is_late) return <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">Late</span>;
        if (att.is_undertime) return <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">Undertime</span>;
        return <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">Present</span>;
      }
    },
    { key: 'remarks', label: 'Remarks' },
    {
      key: 'actions',
      label: 'Actions',
      render: (att) => (
        <div className="flex items-center space-x-3">
          <button
            onClick={() => openEditModal(att)}
            className="text-blue-600 hover:text-blue-800 font-medium text-sm"
          >
            Edit
          </button>
          <button
            onClick={() => handleDelete(att)}
            className="text-red-500 hover:text-red-700 font-medium text-sm"
            title="Delete"
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Attendance</h1>
        <div className="flex space-x-3">
          <Button variant="secondary" onClick={() => setIsSyncModalOpen(true)}>
            <CloudArrowDownIcon className="h-5 w-5 mr-2" />
            Sync Crosschex Cloud
          </Button>
          <Button variant="secondary" onClick={() => setIsImportModalOpen(true)}>
            <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
            Import CSV
          </Button>
          <Button variant="secondary" onClick={() => setIsBulkModalOpen(true)}>
            <CalendarIcon className="h-5 w-5 mr-2" />
            Bulk Entry
          </Button>
          <Button onClick={openModal}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Attendance
          </Button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-800">Attendance Records</h2>
            <span className="text-sm text-gray-500">
              {attendances.length} record{attendances.length !== 1 ? 's' : ''} found
            </span>
          </div>
          <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Search Employee</label>
              <input
                type="text"
                placeholder="Search by name or employee number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit">Search</Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setSearchTerm('');
                }}
              >
                Clear
              </Button>
            </div>
          </form>
        </div>

        <DataTable
          columns={columns}
          data={attendances}
          loading={loading}
        />
      </div>

      {/* Add/Edit Attendance Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingId ? "Edit Attendance" : "Add Attendance"}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Employee *
            </label>
            <select
              required
              value={formData.employee_id}
              onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select Employee</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.employee_no} - {emp.full_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim()}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Date *
            </label>
            <input
              type="date"
              required
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Time In
              </label>
              <input
                type="time"
                value={formData.time_in}
                onChange={(e) => handleTimeInChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">Work starts at 8:00 AM</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Time Out
              </label>
              <input
                type="time"
                value={formData.time_out}
                onChange={(e) => handleTimeOutChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">Regular end: 5PM, OT starts: 7PM</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Late Minutes
              </label>
              <input
                type="number"
                min="0"
                value={formData.late_minutes}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Auto-calculated from Time In
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Overtime Hours
              </label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={formData.overtime_hours}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Auto-calculated (after 7PM)
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_absent}
                onChange={(e) => setFormData({ ...formData, is_absent: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                Absent
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_undertime}
                onChange={(e) => setFormData({ ...formData, is_undertime: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                Undertime
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Remarks
            </label>
            <textarea
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              rows={2}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit">Create</Button>
          </div>
        </form>
      </Modal>

      {/* Bulk Entry Modal */}
      <Modal
        isOpen={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        title="Bulk Attendance Entry"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date for Bulk Entry</label>
            <input
              type="date"
              value={bulkDate}
              onChange={(e) => setBulkDate(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <p className="text-sm text-gray-600">
            This will create attendance records for all active employees on {formatDate(bulkDate)} with default time (8:00 AM - 5:00 PM).
          </p>
          <div className="bg-blue-50 p-4 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>{employees.length}</strong> employees will be marked as present.
            </p>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsBulkModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleBulkEntry}>
              Create All Records
            </Button>
          </div>
        </div>
      </Modal>

      {/* Import Crosschex Modal */}
      <Modal
        isOpen={isImportModalOpen}
        onClose={() => {
          setIsImportModalOpen(false);
          setImportResult(null);
        }}
        title="Import from Crosschex Cloud"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-md">
            <h4 className="text-sm font-medium text-blue-800 mb-2">How to export from Crosschex Cloud:</h4>
            <ol className="text-sm text-blue-700 list-decimal list-inside space-y-1">
              <li>Login to your Crosschex Cloud account</li>
              <li>Go to Reports → Attendance Records</li>
              <li>Select your date range and employees</li>
              <li>Click Export to download the CSV file</li>
              <li>Upload the CSV file below</li>
            </ol>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Crosschex CSV File
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleImportCrosschex}
              disabled={importing}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          {importing && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-sm text-gray-600">Importing...</span>
            </div>
          )}

          {importResult && (
            <div className={`p-4 rounded-md ${importResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
              {importResult.success ? (
                <>
                  <h4 className="text-sm font-medium text-green-800">Import Successful!</h4>
                  <p className="text-sm text-green-700 mt-1">
                    Created: <strong>{importResult.created}</strong> records<br />
                    Updated: <strong>{importResult.updated}</strong> records
                  </p>
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-yellow-700">Warnings:</p>
                      <ul className="text-xs text-yellow-600 list-disc list-inside">
                        {importResult.errors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h4 className="text-sm font-medium text-red-800">Import Failed</h4>
                  <p className="text-sm text-red-700 mt-1">{importResult.error}</p>
                </>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsImportModalOpen(false);
                setImportResult(null);
              }}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* Crosschex Cloud Sync Modal */}
      <Modal
        isOpen={isSyncModalOpen}
        onClose={() => {
          setIsSyncModalOpen(false);
          setSyncResult(null);
        }}
        title="Sync from Crosschex Cloud"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">Crosschex Cloud API Integration</h4>
            <p className="text-sm text-blue-700">
              Connect directly to your Crosschex Cloud account to sync attendance records automatically.
              Your API credentials are stored locally in your browser.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">API Key *</label>
            <input
              type="text"
              value={crosschexConfig.api_key}
              onChange={(e) => setCrosschexConfig({...crosschexConfig, api_key: e.target.value})}
              placeholder="Enter your Crosschex Cloud API Key"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">API Secret *</label>
            <input
              type="password"
              value={crosschexConfig.api_secret}
              onChange={(e) => setCrosschexConfig({...crosschexConfig, api_secret: e.target.value})}
              placeholder="Enter your Crosschex Cloud API Secret"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Start Date</label>
              <input
                type="date"
                value={crosschexConfig.start_date}
                onChange={(e) => setCrosschexConfig({...crosschexConfig, start_date: e.target.value})}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">End Date</label>
              <input
                type="date"
                value={crosschexConfig.end_date}
                onChange={(e) => setCrosschexConfig({...crosschexConfig, end_date: e.target.value})}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          {syncResult && (
            <div className={`p-4 rounded-lg ${syncResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
              {syncResult.success ? (
                <div>
                  <p className="text-green-800 font-medium">
                    {syncResult.message || `Successfully synced ${syncResult.synced} attendance records!`}
                  </p>
                  {syncResult.total_records !== undefined && (
                    <p className="text-green-700 text-sm mt-1">
                      Total records fetched: {syncResult.total_records} | Synced: {syncResult.synced}
                    </p>
                  )}
                  {syncResult.devices && syncResult.devices.length > 0 && (
                    <div className="mt-2">
                      <p className="text-green-700 text-sm font-medium">Devices found:</p>
                      <ul className="text-green-700 text-sm list-disc list-inside">
                        {syncResult.devices.map((d, i) => (
                          <li key={i}>{d.name || d.sn || `Device ${i+1}`}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {syncResult.errors && syncResult.errors.length > 0 && (
                    <div className="mt-2">
                      <p className="text-yellow-700 text-sm font-medium">Warnings:</p>
                      <ul className="text-yellow-700 text-xs list-disc list-inside max-h-20 overflow-y-auto">
                        {syncResult.errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-red-800">{syncResult.error}</p>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setIsSyncModalOpen(false);
                setSyncResult(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={handleTestCrosschex}
              disabled={syncing}
            >
              {syncing ? 'Testing...' : 'Test Connection'}
            </Button>
            <Button
              onClick={handleSyncCrosschex}
              disabled={syncing || !crosschexConfig.api_key || !crosschexConfig.api_secret}
            >
              {syncing ? 'Syncing...' : 'Sync Attendance'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
